import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { USER, URL } from "../../constants/constant";
import "./leaderboard.css";

const PhleboLeaderboard = ({ isEmbedded = false }) => {
	const history = useHistory();
	const [leaderboardData, setLeaderboardData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [viewType, setViewType] = useState("pan-india");
	const [currentPhlebo, setCurrentPhlebo] = useState({});
	const [selectedRow, setSelectedRow] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [selectedMonth, setSelectedMonth] = useState(new Date());
	const [monthOptions, setMonthOptions] = useState([]);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	useEffect(() => {
		generateMonthOptions();
		fetchLeaderboardData(new Date());
	}, []);

	const generateMonthOptions = () => {
		const months = [];
		const today = new Date();
		for (let i = 0; i < 4; i++) {
			const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
			months.push(date);
		}
		setMonthOptions(months);
	};

	const handleBack = () => {
		history.goBack();
	};

	const handleRowClick = (row) => {
		setSelectedRow(row);
		setShowModal(true);
	};

	const closeModal = () => {
		setShowModal(false);
		setSelectedRow(null);
	};

	const formatDate = (date) => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	const fetchLeaderboardData = async (monthDate = selectedMonth) => {
		setLoading(true);
		setError(null);

		try {
			const startOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
			const endOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

			const response = await fetch(
				`${URL.dev_url}upsell-leaderboard?startDate=${formatDate(startOfMonth)}&endDate=${formatDate(endOfMonth)}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						accessToken: USER.access_token,
					},
				},
			);

			if (!response.ok) {
				throw new Error("Failed to fetch leaderboard data");
			}

			const data = await response.json().then(res => res.data || []);

			const groupedByPhlebo = new Map();
			data.forEach(row => {
				const id = String(row.phleboId);
				const rowAmount = (row.pgAmount || 0) + (row.walletAmount || 0);

				if (!groupedByPhlebo.has(id)) {
					groupedByPhlebo.set(id, {
						...row,
						pgAmount: row.pgAmount || 0,
						walletAmount: row.walletAmount || 0,
						totalOrders: row.totalOrders || 0,
						_maxAmount: rowAmount,
					});
					return;
				}

				const existing = groupedByPhlebo.get(id);
				existing.pgAmount += row.pgAmount || 0;
				existing.walletAmount += row.walletAmount || 0;
				existing.totalOrders += row.totalOrders || 0;

				if (rowAmount > existing._maxAmount) {
					Object.assign(existing, row, {
						pgAmount: existing.pgAmount,
						walletAmount: existing.walletAmount,
						totalOrders: existing.totalOrders,
					});
					existing._maxAmount = rowAmount;
				}
			});

			const dedupedData = Array.from(groupedByPhlebo.values()).map(({ _maxAmount, ...rest }) => rest);

			dedupedData.sort((a, b) => {
				const amountA = (a.pgAmount || 0) + (a.walletAmount || 0);
				const amountB = (b.pgAmount || 0) + (b.walletAmount || 0);
				return amountB - amountA;
			});

			const userPhleboid = USER.user?.phleboid || USER.user?.phleboId;
			const currentPhleboData = dedupedData.find(row => String(row.phleboId) === String(userPhleboid));

			if (currentPhleboData) {
				setCurrentPhlebo(prev => ({ ...prev, ...currentPhleboData }));
			}

			setLeaderboardData(dedupedData);
			setLoading(false);
		} catch (error) {
			setError(error.message);
			setLoading(false);
		}
	};

	const handleMonthChange = (monthDate) => {
		setSelectedMonth(monthDate);
		fetchLeaderboardData(monthDate);
	};

	const getMonthLabel = (date) => {
		const months = [
			"Jan",
			"Feb",
			"Mar",
			"Apr",
			"May",
			"Jun",
			"Jul",
			"Aug",
			"Sep",
			"Oct",
			"Nov",
			"Dec",
		];
		return `${months[date.getMonth()]} ${date.getFullYear()}`;
	};

	const toggleView = (type) => {
		setViewType(type);
	};

	const formatName = (name) => {
		if (!name) return '';
		return name
			.split(' ')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ');
	};

	const getFilteredData = (() => {
		if (viewType === "city" && (!currentPhlebo || !currentPhlebo.city)) {
			return [];
		}

		const filtered = viewType === "city"
			? leaderboardData.filter(row => row.city === currentPhlebo.city)
			: leaderboardData;

		const top50 = filtered.slice(0, 50);
		const userInTop50 = top50.some(row => row.phleboId === currentPhlebo.phleboId);

		if (!userInTop50) {
			const userEntry = filtered.find(row => row.phleboId === currentPhlebo.phleboId);
			if (userEntry) top50.push(userEntry);
		}

		return top50;
	})();

	const currentUserRank = (() => {
		if (!currentPhlebo || currentPhlebo.phleboId === undefined) return -1;

		const pool = viewType === "city"
			? leaderboardData.filter(row => row.city === currentPhlebo.city)
			: leaderboardData;

		return pool.findIndex(row => row.phleboId === currentPhlebo.phleboId);
	})();

	const getCurrentMonth = () => {
		const months = [
			"January",
			"February",
			"March",
			"April",
			"May",
			"June",
			"July",
			"August",
			"September",
			"October",
			"November",
			"December",
		];
		const currentDate = new Date();
		return `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
	};

	const getRankIcon = (index) => {
		const medals = { 0: '🥇', 1: '🥈', 2: '🥉' };
		const medal = medals[index];
		return medal
			? <span className={`rank-medal ${['gold-medal', 'silver-medal', 'bronze-medal'][index]}`}>{medal}</span>
			: <span className="rank-number">{index + 1}</span>;
	};

	return (
		<div className="leaderboard-container">
			{!isEmbedded && (
				<header className="leaderboard-header">
					<nav className="navbar navbar-dark bg-primary">
						<button className="back-button" onClick={handleBack}>
							<i className="fa fa-arrow-left"></i>
						</button>
						<div className="navbar-brand">
							<h2 className="leaderboard-title">
								Phlebo Leaderboard - {getCurrentMonth()}
							</h2>
						</div>
					</nav>
				</header>
			)}

			{!loading && !error && (
				<div className="toggle-container">
					<div className="toggle-buttons-group">
						<button
							className={`toggle-btn ${viewType === "pan-india" ? "active" : ""}`}
							onClick={() => toggleView("pan-india")}
						>
							PAN India
						</button>
						<button
							className={`toggle-btn ${viewType === "city" ? "active" : ""}`}
							onClick={() => toggleView("city")}
						>
							My City
						</button>
					</div>
					<div className="month-selector-inline">
						<div className="custom-dropdown-wrapper">
							<button
								className="month-select-button"
								onClick={() => setIsDropdownOpen(!isDropdownOpen)}
							>
								{getMonthLabel(selectedMonth)}
								<span className="dropdown-arrow">▼</span>
							</button>
							{isDropdownOpen && (
								<div className="custom-dropdown-menu">
									{monthOptions.map((month, index) => (
										<div
											key={index}
											className={`dropdown-option ${selectedMonth.getTime() === month.getTime() ? 'selected' : ''}`}
											onClick={() => {
												handleMonthChange(month);
												setIsDropdownOpen(false);
											}}
										>
											{getMonthLabel(month)}
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			)}

			{!loading && !error && currentPhlebo && currentPhlebo.phleboId !== undefined && currentUserRank !== -1 && (
				<div className="my-rank-card">
					<div className="my-rank-left">
						<span className="my-rank-badge">{getRankIcon(currentUserRank)}</span>
						<span className="my-rank-name">{formatName(currentPhlebo.name)}</span>
						{currentPhlebo.city && (
							<span className="my-rank-city">{currentPhlebo.city}</span>
						)}
					</div>
					<div className="my-rank-stats">
						<div className="my-rank-stat">
							<span className="my-rank-stat-label">Tests</span>
							<span className="my-rank-stat-value">{currentPhlebo.totalOrders}</span>
						</div>
						<div className="my-rank-stat">
							<span className="my-rank-stat-label">Amount</span>
							<span className="my-rank-stat-value">
								{currentPhlebo.pgAmount !== undefined && currentPhlebo.walletAmount !== undefined
									? `₹ ${(currentPhlebo.pgAmount + currentPhlebo.walletAmount).toFixed(0)}`
									: "-"}
							</span>
						</div>
					</div>
				</div>
			)}

			{loading && (
				<div className="loading-container">
					<div className="spinner"></div>
					<p>Loading leaderboard...</p>
				</div>
			)}

			{error && (
				<div className="error-container">
					<p className="error-message">Error: {error}</p>
				</div>
			)}

			{!loading && !error && (
				<div className="metric-card-container">
					<div className="metric-card fade-in">
						<div className="card-header">
							{viewType === "pan-india"
								? "PAN India Rankings"
								: `${currentPhlebo?.city || "City"} Rankings`}
						</div>
						<div className="card-body">
							<table className={`leaderboard-table ${viewType === "city" ? "city-view" : ""}`}>
								<colgroup>
									{viewType === "pan-india" ? (
										<>
											<col style={{ width: '15%' }} />
											<col style={{ width: '20%' }} />
											<col style={{ width: '20%' }} />
											<col style={{ width: '15%' }} />
											<col style={{ width: '30%' }} />
										</>
									) : (
										<>
											<col style={{ width: '15%' }} />
											<col style={{ width: '30%' }} />
											<col style={{ width: '25%' }} />
											<col style={{ width: '30%' }} />
										</>
									)}
								</colgroup>
								<thead>
									<tr>
										<th>Rank</th>
										<th>Name</th>
										{viewType === "pan-india" && <th>City</th>}
										<th>Tests</th>
										<th>Amount</th>
									</tr>
								</thead>
								<tbody>
									{getFilteredData.length > 0 ? (
										getFilteredData.map((row, index) => {
											const actualRank = viewType === "pan-india" ? leaderboardData.findIndex(r => r.phleboId === row.phleboId) : index;
											const isCurrentUser = row.phleboId === currentPhlebo.phleboId;
											const isInTop50 = index < 50;
											const totalAmount = row.pgAmount !== undefined && row.walletAmount !== undefined ? `₹ ${(row.pgAmount + row.walletAmount).toFixed(0)}` : "-";

											return (
												<tr
													key={`${viewType}-${row.phleboId}-${index}`}
													className={`table-row ${isCurrentUser ? (isInTop50 ? 'user-highlighted' : 'user-bottom') : ''}`}
													onClick={() => handleRowClick(row)}
													style={{ cursor: 'pointer' }}
												>
													<td className="rank-cell">{getRankIcon(actualRank)}</td>
													<td className="name-cell">
														{formatName(row.name)}
														{isCurrentUser && <span className="you-badge">YOU</span>}
													</td>
													{viewType === "pan-india" && <td className="city-cell city-cell-left">{row.city}</td>}
													<td className="orders-cell"><span className="orders-badge">{row.totalOrders}</span></td>
													<td className="amount-cell"><span className="amount-badge">{totalAmount}</span></td>
												</tr>
											);
										})
									) : (
										<tr>
											<td colSpan={viewType === "pan-india" ? 5 : 4} className="no-data">No data available</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}

			{showModal && selectedRow && (
				<div className="modal-overlay" onClick={closeModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h2>Phlebo Details</h2>
							<button className="modal-close-btn" onClick={closeModal}>
								×
							</button>
						</div>
						<div className="modal-body">
							<div className="detail-row">
								<span className="detail-label">Full Name:</span>
								<span className="detail-value">{formatName(selectedRow.name)}</span>
							</div>
							<div className="detail-row">
								<span className="detail-label">Phlebo ID:</span>
								<span className="detail-value">{selectedRow.phleboId}</span>
							</div>
							{selectedRow.city && (
								<div className="detail-row">
									<span className="detail-label">City:</span>
									<span className="detail-value">{selectedRow.city}</span>
								</div>
							)}
							<div className="detail-row">
								<span className="detail-label">Total Tests:</span>
								<span className="detail-value">{selectedRow.totalOrders}</span>
							</div>
							{selectedRow.pgAmount !== undefined && selectedRow.walletAmount !== undefined && (
								<div className="detail-row">
									<span className="detail-label">Total Amount:</span>
									<span className="detail-value">₹ {(selectedRow.pgAmount + selectedRow.walletAmount).toFixed(0)}</span>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PhleboLeaderboard;
